import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FaKeyboard, FaTimes } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import "./SearchWord.css";
import {
  getEnglishBookName,
  getTamilBookName,
  getDeutschBookName,
} from "../BookNames";
import { AuthContext } from "../../Authentication/AuthContext";
import {
  getBookChapterCount,
  getChapterVerses,
  getStaticBibleSections,
  getWholeBookVerses,
  prefetchWholeBookInBackground,
  startBibleDownloadCache,
} from "../DownloadCache";
import TamilKeyboard from "./TamilKeyboard";

const ALL_VALUE = "all";
const SEARCH_CONCURRENCY = 10;

const normalizeLanguageKey = (appLanguage) => {
  const normalizedLanguage = String(appLanguage || "")
    .trim()
    .toLowerCase();

  if (normalizedLanguage === "tamil" || normalizedLanguage === "தமிழ்") {
    return "tamil";
  }

  if (normalizedLanguage === "deutsch" || normalizedLanguage === "german") {
    return "deutsch";
  }

  return "english";
};

const normalizeIncomingLanguageKey = (languageValue) => {
  const normalizedValue = String(languageValue || "")
    .trim()
    .toLowerCase();

  if (!normalizedValue) return "";

  if (normalizedValue === "tamil" || normalizedValue === "தமிழ்") {
    return "tamil";
  }

  if (normalizedValue === "english") {
    return "english";
  }

  if (normalizedValue === "deutsch" || normalizedValue === "german") {
    return "deutsch";
  }

  return "";
};

const getAllSelectText = (appLanguage) => {
  if (appLanguage === "English") return "All / Select";
  if (appLanguage === "Deutsch") return "Alle / Select";
  return "அனைத்தும் / Select";
};

const getSelectedLanguagesFromHome = ({
  statePrefill,
  params,
  fallbackLanguage,
}) => {
  const stateLanguages = Array.isArray(statePrefill.selectedLanguages)
    ? statePrefill.selectedLanguages
    : [];

  const queryLanguages = String(params.get("languages") || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  const incomingLanguages =
    stateLanguages.length > 0 ? stateLanguages : queryLanguages;

  const selectedHomeLanguages = incomingLanguages.reduce(
    (uniqueLanguages, item) => {
      const normalizedLanguage = normalizeIncomingLanguageKey(item);

      if (normalizedLanguage && !uniqueLanguages.includes(normalizedLanguage)) {
        uniqueLanguages.push(normalizedLanguage);
      }

      return uniqueLanguages;
    },
    [],
  );

  if (selectedHomeLanguages.length > 0) {
    return selectedHomeLanguages;
  }

  return [normalizeLanguageKey(fallbackLanguage)];
};

const focusWithoutScrolling = (element) => {
  if (!element) return;

  try {
    element.focus({ preventScroll: true });
  } catch (error) {
    element.focus();
  }
};

const getBookCacheKey = (bookValue) =>
  String(bookValue || "")
    .trim()
    .toLowerCase();

const getCompactBookKey = (bookValue) =>
  String(bookValue || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const resolveBookKeyFromBooks = (books, bookValue, fallbackValue = "") => {
  if (!bookValue) return fallbackValue;
  if (bookValue === ALL_VALUE) return ALL_VALUE;

  const incomingCompactKey = getCompactBookKey(bookValue);
  const matchedBook = books.find(
    (book) => getCompactBookKey(book.book_name) === incomingCompactKey,
  );

  if (matchedBook) return getBookCacheKey(matchedBook.book_name);
  return getBookCacheKey(bookValue);
};

const getOriginalBookNameFromBooks = (books, bookValue) => {
  if (!bookValue || bookValue === ALL_VALUE) return "";

  const incomingCompactKey = getCompactBookKey(bookValue);
  const matchedBook = books.find(
    (book) => getCompactBookKey(book.book_name) === incomingCompactKey,
  );

  return matchedBook?.book_name || bookValue;
};

const getLanguageText = (lang) => {
  if (lang === "tamil") return "Tamil";
  if (lang === "deutsch") return "Deutsch";
  return "English";
};

const getBookDisplayName = (bookName, appLanguage) => {
  if (appLanguage === "English") return getEnglishBookName(bookName);
  if (appLanguage === "Deutsch") return getDeutschBookName(bookName);
  return getTamilBookName(bookName);
};

const getTestamentDisplayName = (testamentName, appLanguage) => {
  if (appLanguage === "English") return testamentName;

  if (appLanguage === "Deutsch") {
    return testamentName === "Old Testament"
      ? "Altes Testament"
      : "Neues Testament";
  }

  return testamentName === "Old Testament" ? "பழைய ஏற்பாடு" : "புதிய ஏற்பாடு";
};

const getLabels = (appLanguage) => {
  if (appLanguage === "English") {
    return {
      testament: "Testament",
      book: "Book",
      chapter: "Chapter",
      searchWord: "Word",
      language: "Language",
      select: "Select",
      searchWordPlaceholder: "Type the word",
      note: "choose “All / Select” to search the word in both testaments. Otherwise, choose one testament, one book, or one chapter, then enter the word.",
      submit: "Submit",
      reset: "Reset",
      noResults: "No verses found matching the criteria.",
      selectTestamentAlert: "Please select the testament.",
      enterWordAlert: "Please enter the word.",
      selectLanguageAlert: "Please select at least one language.",
      alertTitle: "Required",
      keyboard: "Keyboard",
      closeKeyboard: "Close keyboard",
      searchResult: "Search Result",
      wordLabel: "Word",
      oldTestament: "Old Testament",
      newTestament: "New Testament",
      found: "Found",
    };
  }

  if (appLanguage === "Deutsch") {
    return {
      testament: "Testament",
      book: "Buch",
      chapter: "Kapitel",
      searchWord: "Wort",
      language: "Sprache",
      select: "Select",
      searchWordPlaceholder: "Wort eingeben",
      note: "„Alle / Select“ auswählen, um das Wort in beiden Testamenten zu suchen. Andernfalls ein Testament, ein Buch oder ein Kapitel auswählen und das Wort eingeben.",
      submit: "Submit",
      reset: "Reset",
      noResults: "Keine Verse gefunden.",
      selectTestamentAlert: "Bitte Testament auswählen.",
      enterWordAlert: "Bitte Wort eingeben.",
      selectLanguageAlert: "Bitte mindestens eine Sprache auswählen.",
      alertTitle: "Required",
      keyboard: "Tastatur",
      closeKeyboard: "Tastatur schließen",
      searchResult: "Suchergebnis",
      wordLabel: "Wort",
      oldTestament: "Altes Testament",
      newTestament: "Neues Testament",
      found: "Gefunden",
    };
  }

  return {
    testament: "ஏற்பாடு",
    book: "ஆகமம்",
    chapter: "அதிகாரம்",
    searchWord: "வார்த்தை",
    language: "மொழி",
    select: "Select",
    searchWordPlaceholder: "சொல்லை உள்ளிடவும்",
    note: "அனைத்தும் என்பதை தேர்ந்தெடுத்தால் இரு ஏற்பாடுகளிலும் சொல்லைத் தேடும். அல்லது ஒரு ஏற்பாடு, ஒரு ஆகமம், அல்லது ஒரு அதிகாரத்தையும் தேர்ந்தெடுத்து சொல்லை உள்ளிடவும்.",
    submit: "Submit",
    reset: "Reset",
    noResults: "தேடிய வார்த்தைக்கு வசனங்கள் கிடைக்கவில்லை.",
    selectTestamentAlert: "ஏற்பாட்டைத் தேர்ந்தெடுக்கவும்.",
    enterWordAlert: "சொல்லை உள்ளிடவும்.",
    selectLanguageAlert: "குறைந்தது ஒரு மொழியைத் தேர்ந்தெடுக்கவும்.",
    alertTitle: "தேவை",
    keyboard: "விசைப்பலகை",
    closeKeyboard: "விசைப்பலகையை மூடு",
    searchResult: "தேடல் முடிவு",
    wordLabel: "வார்த்தை",
    oldTestament: "பழைய ஏற்பாடு",
    newTestament: "புதிய ஏற்பாடு",
    found: "கிடைத்தவை",
  };
};

const escapeRegExp = (value) =>
  `${value}`.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const buildHighlightedParts = (text, searchWord) => {
  const safeText = text || "";
  const trimmedSearchWord = searchWord.trim();

  if (!trimmedSearchWord) return [safeText];

  const regex = new RegExp(`(${escapeRegExp(trimmedSearchWord)})`, "gi");

  return safeText.split(regex).filter((part) => part !== "");
};

const getTestamentTypeFromName = (testamentName) =>
  testamentName === "Old Testament" ? "old" : "new";

const getTestamentSectionFromType = (testaments, testamentType) => {
  if (!testamentType || testamentType === ALL_VALUE) return null;

  return testaments.find(
    (testament) =>
      getTestamentTypeFromName(testament.testament) === testamentType,
  );
};

const CustomSelect = ({
  id,
  value,
  options,
  placeholder,
  onChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  const selectedOption = options.find((option) => option.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!selectRef.current || selectRef.current.contains(event.target)) {
        return;
      }

      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, []);

  const handleOptionClick = (nextValue) => {
    onChange(nextValue);
    setIsOpen(false);
  };

  return (
    <div className="searchword-custom-select" ref={selectRef}>
      <button
        id={id}
        type="button"
        disabled={disabled}
        className={`searchword-custom-select-button ${
          value ? "has-selected-value" : ""
        }`}
        onClick={() => setIsOpen((previousValue) => !previousValue)}
      >
        <span className="searchword-custom-select-text">{displayText}</span>

        {!value || value === ALL_VALUE ? (
          <span className="searchword-custom-select-arrow" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              className="searchword-custom-select-arrow-icon"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </span>
        ) : null}
      </button>

      {isOpen && !disabled ? (
        <div className="searchword-custom-select-options">
          {options.map((option) => (
            <button
              key={`${id}-${option.value}`}
              type="button"
              className={`searchword-custom-select-option ${
                option.value === value ? "active" : ""
              }`}
              onClick={() => handleOptionClick(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

function SearchWord() {
  const { language } = useContext(AuthContext);
  const location = useLocation();

  const pageScrollRef = useRef(null);
  const formContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchInputRowRef = useRef(null);
  const keyboardPanelRef = useRef(null);
  const keyboardToggleRef = useRef(null);
  const resultContainerRef = useRef(null);
  const resultSummaryRef = useRef(null);
  const homePrefillAppliedRef = useRef(false);
  const searchRunIdRef = useRef(0);
  const pageTouchLastYRef = useRef(0);
  const bodyScrollYRef = useRef(0);
  const activeNativeInputRef = useRef(null);
  const inputFocusScrollTopRef = useRef(0);

  const [testaments, setTestaments] = useState([]);
  const [selectedTestament, setSelectedTestament] = useState(ALL_VALUE);
  const [selectedBook, setSelectedBook] = useState(ALL_VALUE);
  const [selectedChapter, setSelectedChapter] = useState(ALL_VALUE);
  const [searchWord, setSearchWord] = useState("");
  const [verses, setVerses] = useState([]);
  const [error, setError] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [resetSignal, setResetSignal] = useState(0);
  const [shouldAutoSubmit, setShouldAutoSubmit] = useState(false);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [isNativeInputFocused, setIsNativeInputFocused] = useState(false);
  const [keyboardPosition, setKeyboardPosition] = useState({
    top: 0,
    maxHeight: 0,
    ready: false,
  });
  const [selectedLanguages, setSelectedLanguages] = useState([
    normalizeLanguageKey(language),
  ]);

  const labels = useMemo(() => getLabels(language), [language]);
  const allSelectText = useMemo(() => getAllSelectText(language), [language]);
  const availableLanguages = useMemo(() => ["tamil", "english", "deutsch"], []);
  const isTamilDefaultLanguage = useMemo(
    () => normalizeLanguageKey(language) === "tamil",
    [language],
  );

  const rememberSearchWordInputScrollPosition = useCallback(() => {
    if (pageScrollRef.current) {
      inputFocusScrollTopRef.current = pageScrollRef.current.scrollTop;
    }
  }, []);

  const restoreSearchWordInputScrollPosition = useCallback(() => {
    const scroller = pageScrollRef.current;

    if (!scroller) return;

    const savedScrollTop = inputFocusScrollTopRef.current;

    window.requestAnimationFrame(() => {
      scroller.scrollTop = savedScrollTop;

      window.setTimeout(() => {
        scroller.scrollTop = savedScrollTop;
      }, 60);

      window.setTimeout(() => {
        scroller.scrollTop = savedScrollTop;
      }, 180);

      window.setTimeout(() => {
        scroller.scrollTop = savedScrollTop;
      }, 360);
    });
  }, []);

  const handleNativeInputFocus = useCallback((event) => {
    const input = event.currentTarget;

    activeNativeInputRef.current = input;
    setIsNativeInputFocused(true);

    window.setTimeout(() => {
      if (!input) return;

      try {
        input.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      } catch (error) {
        input.scrollIntoView();
      }
    }, 180);
  }, []);

  const handleNativeInputBlur = useCallback(() => {
    window.setTimeout(() => {
      const activeElement = document.activeElement;

      if (
        activeElement &&
        activeElement.closest &&
        activeElement.closest(".searchword-container input[type='text']")
      ) {
        return;
      }

      activeNativeInputRef.current = null;
      setIsNativeInputFocused(false);
    }, 180);
  }, []);

  const moveSearchCursorToEnd = useCallback((nextWord) => {
    window.requestAnimationFrame(() => {
      const input = searchInputRef.current;

      if (!input) return;

      focusWithoutScrolling(input);

      const nextPosition = nextWord.length;

      if (typeof input.setSelectionRange === "function") {
        input.setSelectionRange(nextPosition, nextPosition);
      }
    });
  }, []);

  const updateKeyboardPosition = useCallback(() => {
    if (
      !formContainerRef.current ||
      !searchInputRowRef.current ||
      !keyboardPanelRef.current
    ) {
      return;
    }

    const containerRect = formContainerRef.current.getBoundingClientRect();
    const inputRowRect = searchInputRowRef.current.getBoundingClientRect();
    const keyboardRect = keyboardPanelRef.current.getBoundingClientRect();

    const edgePadding = 16;
    const gap = 10;
    const minimumUsableHeight = 92;

    const inputTop = inputRowRect.top - containerRect.top;
    const inputBottom = inputRowRect.bottom - containerRect.top;
    const naturalKeyboardHeight = keyboardRect.height || 180;

    const aboveAvailableHeight = Math.floor(inputTop - edgePadding - gap);
    const belowAvailableHeight = Math.floor(
      containerRect.height - inputBottom - edgePadding - gap,
    );

    let nextTop = edgePadding;
    let nextMaxHeight = Math.max(60, aboveAvailableHeight);

    if (
      aboveAvailableHeight >= minimumUsableHeight ||
      aboveAvailableHeight >= belowAvailableHeight
    ) {
      nextMaxHeight = Math.max(60, aboveAvailableHeight);

      const visibleKeyboardHeight = Math.min(
        naturalKeyboardHeight,
        nextMaxHeight,
      );

      nextTop = Math.max(edgePadding, inputTop - gap - visibleKeyboardHeight);
    } else {
      nextMaxHeight = Math.max(60, belowAvailableHeight);

      const lowestSafeTop = Math.max(
        edgePadding,
        containerRect.height - edgePadding - nextMaxHeight,
      );

      nextTop = Math.min(inputBottom + gap, lowestSafeTop);
    }

    setKeyboardPosition({
      top: Math.round(nextTop),
      maxHeight: Math.round(nextMaxHeight),
      ready: true,
    });
  }, []);

  useEffect(() => {
    if (!isTamilDefaultLanguage) {
      setShowKeyboard(false);
    }
  }, [isTamilDefaultLanguage]);

  useEffect(() => {
    if (!showKeyboard) {
      setKeyboardPosition({
        top: 0,
        maxHeight: 0,
        ready: false,
      });

      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      updateKeyboardPosition();
    });

    window.addEventListener("resize", updateKeyboardPosition);
    window.addEventListener("orientationchange", updateKeyboardPosition);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", updateKeyboardPosition);
      window.removeEventListener("orientationchange", updateKeyboardPosition);
    };
  }, [showKeyboard, updateKeyboardPosition]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!showKeyboard) return;

      if (
        keyboardPanelRef.current &&
        keyboardPanelRef.current.contains(event.target)
      ) {
        return;
      }

      if (
        keyboardToggleRef.current &&
        keyboardToggleRef.current.contains(event.target)
      ) {
        return;
      }

      setShowKeyboard(false);
    };

    document.addEventListener("mousedown", handleOutsideClick, true);
    document.addEventListener("touchstart", handleOutsideClick, true);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick, true);
      document.removeEventListener("touchstart", handleOutsideClick, true);
    };
  }, [showKeyboard]);

  useEffect(() => {
    const updateMobileViewportVariables = () => {
      const visualViewport = window.visualViewport;

      const visualViewportHeight =
        visualViewport && visualViewport.height
          ? visualViewport.height
          : window.innerHeight;

      const isInputFocused = Boolean(activeNativeInputRef.current);

      const bottomBrowserSpace =
        !isInputFocused && visualViewport && visualViewport.height
          ? Math.max(
              0,
              window.innerHeight -
                visualViewport.height -
                visualViewport.offsetTop,
            )
          : 0;

      document.documentElement.style.setProperty(
        "--searchword-visual-viewport-height",
        `${Math.round(visualViewportHeight)}px`,
      );

      document.documentElement.style.setProperty(
        "--searchword-mobile-bottom-space",
        `${Math.round(bottomBrowserSpace)}px`,
      );
    };

    updateMobileViewportVariables();

    window.addEventListener("resize", updateMobileViewportVariables);
    window.addEventListener("orientationchange", updateMobileViewportVariables);

    if (window.visualViewport) {
      window.visualViewport.addEventListener(
        "resize",
        updateMobileViewportVariables,
      );
      window.visualViewport.addEventListener(
        "scroll",
        updateMobileViewportVariables,
      );
    }

    return () => {
      window.removeEventListener("resize", updateMobileViewportVariables);
      window.removeEventListener(
        "orientationchange",
        updateMobileViewportVariables,
      );

      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          "resize",
          updateMobileViewportVariables,
        );
        window.visualViewport.removeEventListener(
          "scroll",
          updateMobileViewportVariables,
        );
      }

      document.documentElement.style.removeProperty(
        "--searchword-visual-viewport-height",
      );
      document.documentElement.style.removeProperty(
        "--searchword-mobile-bottom-space",
      );
    };
  }, []);

  useEffect(() => {
    const stopHeaderTouchDrag = (event) => {
      if (!event.touches || event.touches.length !== 1) {
        return;
      }

      event.preventDefault();
    };

    const headerElements = [
      resultSummaryRef.current,
      document.querySelector(".top-header-shell"),
      document.querySelector(".top-header"),
      document.querySelector(".contact-info"),
    ].filter(Boolean);

    headerElements.forEach((headerElement) => {
      headerElement.addEventListener("touchmove", stopHeaderTouchDrag, {
        passive: false,
      });
    });

    return () => {
      headerElements.forEach((headerElement) => {
        headerElement.removeEventListener("touchmove", stopHeaderTouchDrag);
      });
    };
  }, [verses.length]);

  const scrollToResultHeader = useCallback(() => {
    window.setTimeout(() => {
      if (resultContainerRef.current) {
        resultContainerRef.current.scrollTop = 0;
      }

      if (pageScrollRef.current && resultContainerRef.current) {
        pageScrollRef.current.scrollTo({
          top: resultContainerRef.current.offsetTop,
          behavior: "smooth",
        });

        return;
      }

      resultContainerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 20);
  }, []);

  const homePrefill = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const statePrefill = location.state?.prefill || {};
    const hasHomePrefill =
      params.get("home") === "1" || location.state?.fromHome === true;

    if (!hasHomePrefill) return null;

    return {
      testamentType:
        statePrefill.testamentType || params.get("testament") || ALL_VALUE,
      book: statePrefill.book || params.get("book") || ALL_VALUE,
      chapter: statePrefill.chapter || params.get("chapter") || ALL_VALUE,
      word: statePrefill.word || params.get("word") || "",
      selectedLanguages: getSelectedLanguagesFromHome({
        statePrefill,
        params,
        fallbackLanguage: language,
      }),
    };
  }, [language, location.search, location.state]);

  useEffect(() => {
    startBibleDownloadCache();
    setTestaments(getStaticBibleSections());
  }, []);

  const selectedTestamentObject = useMemo(() => {
    if (selectedTestament === ALL_VALUE) return null;

    return testaments.find(
      (testament) => String(testament.id) === String(selectedTestament),
    );
  }, [selectedTestament, testaments]);

  const selectedTestamentType = useMemo(() => {
    if (!selectedTestamentObject) return null;
    return getTestamentTypeFromName(selectedTestamentObject.testament);
  }, [selectedTestamentObject]);

  const books = useMemo(() => {
    return selectedTestamentObject?.books || [];
  }, [selectedTestamentObject]);

  const selectedOriginalBookName = useMemo(() => {
    return getOriginalBookNameFromBooks(books, selectedBook);
  }, [books, selectedBook]);

  const chapters = useMemo(() => {
    if (!selectedBook || selectedBook === ALL_VALUE || !selectedTestamentType) {
      return [];
    }

    const chapterCount = getBookChapterCount(
      selectedTestamentType,
      selectedBook,
    );

    if (chapterCount <= 0) return [];

    return Array.from({ length: chapterCount }, (_, index) => ({
      id: index + 1,
      chapter_number: index + 1,
    }));
  }, [selectedBook, selectedTestamentType]);

  useEffect(() => {
    if (!testaments.length || !homePrefill || homePrefillAppliedRef.current) {
      return;
    }

    const prefillTestamentType = homePrefill.testamentType || ALL_VALUE;
    const isAllTestaments = prefillTestamentType === ALL_VALUE;

    if (isAllTestaments) {
      setSelectedTestament(ALL_VALUE);
      setSelectedBook(ALL_VALUE);
      setSelectedChapter(ALL_VALUE);
    } else {
      const testamentSection = getTestamentSectionFromType(
        testaments,
        prefillTestamentType,
      );

      if (!testamentSection) return;

      const resolvedBook = resolveBookKeyFromBooks(
        testamentSection.books,
        homePrefill.book,
        ALL_VALUE,
      );

      setSelectedTestament(String(testamentSection.id));
      setSelectedBook(resolvedBook || ALL_VALUE);
      setSelectedChapter(homePrefill.chapter || ALL_VALUE);
    }

    setSearchWord(homePrefill.word || "");
    setSelectedLanguages(
      homePrefill.selectedLanguages || [normalizeLanguageKey(language)],
    );
    setVerses([]);
    setError("");
    setIsSubmitted(false);

    homePrefillAppliedRef.current = true;

    if (homePrefill.word) {
      setShouldAutoSubmit(true);
    }
  }, [homePrefill, language, testaments]);

  const resetFormFields = useCallback(() => {
    searchRunIdRef.current += 1;

    const defaultLanguage = normalizeLanguageKey(language);

    setSelectedTestament(ALL_VALUE);
    setSelectedBook(ALL_VALUE);
    setSelectedChapter(ALL_VALUE);
    setSearchWord("");
    setVerses([]);
    setError("");
    setAlertMessage("");
    setIsSubmitted(false);
    setShouldAutoSubmit(false);
    setShowKeyboard(false);
    setSelectedLanguages([defaultLanguage]);
    setResetSignal((previousValue) => previousValue + 1);
  }, [language]);

  useEffect(() => {
    if (homePrefill) return;
    resetFormFields();
  }, [language, homePrefill, resetFormFields]);

  useEffect(() => {
    if (!selectedBook || selectedBook === ALL_VALUE || !selectedTestamentType) {
      return;
    }

    prefetchWholeBookInBackground(selectedTestamentType, selectedBook);
  }, [selectedBook, selectedTestamentType]);

  const testamentOptions = useMemo(() => {
    return [
      {
        value: ALL_VALUE,
        label: allSelectText,
      },
      ...testaments.map((testament) => ({
        value: String(testament.id),
        label: getTestamentDisplayName(testament.testament, language),
      })),
    ];
  }, [allSelectText, language, testaments]);

  const bookOptions = useMemo(() => {
    return [
      {
        value: ALL_VALUE,
        label: allSelectText,
      },
      ...books.map((book) => {
        const bookKey = getBookCacheKey(book.book_name);

        return {
          value: bookKey,
          label: getBookDisplayName(book.book_name, language),
        };
      }),
    ];
  }, [allSelectText, books, language]);

  const chapterOptions = useMemo(() => {
    return [
      {
        value: ALL_VALUE,
        label: allSelectText,
      },
      ...chapters.map((chapter) => ({
        value: String(chapter.chapter_number),
        label: String(chapter.chapter_number),
      })),
    ];
  }, [allSelectText, chapters]);

  const orderedLanguages = useMemo(() => {
    const selectedSet = new Set(selectedLanguages);

    return [
      ...selectedLanguages,
      ...availableLanguages.filter((lang) => !selectedSet.has(lang)),
    ];
  }, [availableLanguages, selectedLanguages]);

  const resultCounts = useMemo(() => {
    return verses.reduce(
      (counts, verse) => {
        const testamentType = verse.testamentType || "";

        if (testamentType === "old") {
          counts.old += 1;
        }

        if (testamentType === "new") {
          counts.new += 1;
        }

        counts.total += 1;

        return counts;
      },
      {
        old: 0,
        new: 0,
        total: 0,
      },
    );
  }, [verses]);

  const selectedSearchWord = searchWord.trim();

  const showAlert = (message) => {
    setAlertMessage(message);
  };

  const handleTestamentChange = (value) => {
    searchRunIdRef.current += 1;

    setSelectedTestament(value);
    setSelectedBook(ALL_VALUE);
    setSelectedChapter(ALL_VALUE);
    setVerses([]);
    setError("");
    setIsSubmitted(false);
  };

  const handleBookChange = (value) => {
    searchRunIdRef.current += 1;

    const resolvedBook = resolveBookKeyFromBooks(books, value, ALL_VALUE);

    setSelectedBook(resolvedBook || ALL_VALUE);
    setSelectedChapter(ALL_VALUE);
    setVerses([]);
    setError("");
    setIsSubmitted(false);
  };

  const handleChapterChange = (value) => {
    searchRunIdRef.current += 1;

    setSelectedChapter(value || ALL_VALUE);
    setVerses([]);
    setError("");
    setIsSubmitted(false);
  };

  const handleKeyboardToggle = () => {
    if (!isTamilDefaultLanguage) {
      setShowKeyboard(false);
      return;
    }

    rememberSearchWordInputScrollPosition();
    setShowKeyboard((previousValue) => !previousValue);

    window.setTimeout(() => {
      focusWithoutScrolling(searchInputRef.current);

      if (searchInputRef.current) {
        activeNativeInputRef.current = searchInputRef.current;
        setIsNativeInputFocused(true);
        restoreSearchWordInputScrollPosition();
      }
    }, 0);
  };

  const handleKeyboardKeyPress = (key) => {
    setSearchWord((previousValue) => {
      const nextWord = `${previousValue}${key}`;

      moveSearchCursorToEnd(nextWord);

      return nextWord;
    });
  };

  const closeKeyboard = () => {
    setShowKeyboard(false);
  };

  const toggleLanguage = (lang) => {
    setSelectedLanguages((previousValue) => {
      const isSelected = previousValue.includes(lang);

      if (isSelected && previousValue.length === 1) {
        return previousValue;
      }

      if (isSelected) {
        return previousValue.filter((item) => item !== lang);
      }

      return [...previousValue, lang];
    });
  };

  const getBookItemsToSearch = useCallback(() => {
    if (!selectedTestament || selectedTestament === ALL_VALUE) {
      return testaments.flatMap((testament) => {
        const testamentType = getTestamentTypeFromName(testament.testament);

        return testament.books.map((book) => ({
          testamentType,
          bookKey: getBookCacheKey(book.book_name),
          originalBookName: book.book_name,
        }));
      });
    }

    if (!selectedTestamentObject) return [];

    const testamentType = getTestamentTypeFromName(
      selectedTestamentObject.testament,
    );

    if (!selectedBook || selectedBook === ALL_VALUE) {
      return selectedTestamentObject.books.map((book) => ({
        testamentType,
        bookKey: getBookCacheKey(book.book_name),
        originalBookName: book.book_name,
      }));
    }

    return [
      {
        testamentType,
        bookKey: selectedBook,
        originalBookName: selectedOriginalBookName || selectedBook,
      },
    ];
  }, [
    selectedBook,
    selectedOriginalBookName,
    selectedTestament,
    selectedTestamentObject,
    testaments,
  ]);

  const collectVersesForBook = useCallback(
    async (bookItem) => {
      if (selectedChapter && selectedChapter !== ALL_VALUE) {
        const chapterVerses = await getChapterVerses(
          bookItem.testamentType,
          bookItem.bookKey,
          Number(selectedChapter),
        );

        return Array.isArray(chapterVerses)
          ? chapterVerses.map((verse) => ({
              ...verse,
              testamentType: bookItem.testamentType,
              book_name: verse.book_name || bookItem.originalBookName,
            }))
          : [];
      }

      const wholeBookVerses = await getWholeBookVerses(
        bookItem.testamentType,
        bookItem.bookKey,
      );

      return Array.isArray(wholeBookVerses)
        ? wholeBookVerses.map((verse) => ({
            ...verse,
            testamentType: bookItem.testamentType,
            book_name: verse.book_name || bookItem.originalBookName,
          }))
        : [];
    },
    [selectedChapter],
  );

  const filterMatchingVerses = useCallback(
    (bookVerses, currentSearchWord) => {
      const searchTerm = currentSearchWord.trim().toLowerCase();

      if (!searchTerm) return [];

      return bookVerses.filter((verse) => {
        return availableLanguages.some((lang) =>
          String(verse[lang] || "")
            .toLowerCase()
            .includes(searchTerm),
        );
      });
    },
    [availableLanguages],
  );

  const performSearch = useCallback(
    async ({ showValidation = true, shouldScroll = false } = {}) => {
      searchRunIdRef.current += 1;

      const currentSearchRunId = searchRunIdRef.current;

      setShowKeyboard(false);
      setAlertMessage("");
      setError("");
      setVerses([]);
      setIsSubmitted(true);

      if (!selectedTestament) {
        if (showValidation) showAlert(labels.selectTestamentAlert);
        return;
      }

      if (!searchWord.trim()) {
        if (showValidation) showAlert(labels.enterWordAlert);
        return;
      }

      if (!selectedLanguages.length) {
        if (showValidation) showAlert(labels.selectLanguageAlert);
        return;
      }

      if (shouldScroll) {
        scrollToResultHeader();
      }

      const bookItems = getBookItemsToSearch();

      if (!bookItems.length) {
        setError(labels.noResults);
        return;
      }

      const resultsByBookIndex = Array.from(
        { length: bookItems.length },
        () => [],
      );
      let completedCount = 0;
      let foundCount = 0;
      let nextIndex = 0;

      const pushProgressiveResults = () => {
        const nextResults = resultsByBookIndex.flat();

        if (nextResults.length > 0) {
          setVerses(nextResults);
        }
      };

      const runWorker = async () => {
        while (nextIndex < bookItems.length) {
          const currentIndex = nextIndex;
          nextIndex += 1;

          const bookItem = bookItems[currentIndex];

          try {
            const bookVerses = await collectVersesForBook(bookItem);

            if (searchRunIdRef.current !== currentSearchRunId) return;

            const matchedVerses = filterMatchingVerses(bookVerses, searchWord);

            resultsByBookIndex[currentIndex] = matchedVerses;

            if (matchedVerses.length > 0) {
              foundCount += matchedVerses.length;
              pushProgressiveResults();
            }
          } catch (requestError) {
            resultsByBookIndex[currentIndex] = [];
          } finally {
            completedCount += 1;
          }
        }
      };

      const workerCount = Math.min(SEARCH_CONCURRENCY, bookItems.length);

      await Promise.all(Array.from({ length: workerCount }, () => runWorker()));

      if (searchRunIdRef.current !== currentSearchRunId) return;

      const finalResults = resultsByBookIndex.flat();

      setVerses(finalResults);

      if (completedCount === bookItems.length && foundCount === 0) {
        setError(labels.noResults);
      }

      if (shouldScroll && finalResults.length > 0) {
        scrollToResultHeader();
      }
    },
    [
      collectVersesForBook,
      filterMatchingVerses,
      getBookItemsToSearch,
      labels.enterWordAlert,
      labels.noResults,
      labels.selectLanguageAlert,
      labels.selectTestamentAlert,
      scrollToResultHeader,
      searchWord,
      selectedLanguages.length,
      selectedTestament,
    ],
  );

  useEffect(() => {
    if (!shouldAutoSubmit) return;
    if (!selectedTestament || !searchWord.trim()) return;

    setShouldAutoSubmit(false);

    performSearch({
      showValidation: false,
      shouldScroll: false,
    });
  }, [performSearch, searchWord, selectedTestament, shouldAutoSubmit]);

  const handleSubmit = (event) => {
    event.preventDefault();

    setShowKeyboard(false);

    performSearch({
      showValidation: true,
      shouldScroll: true,
    });
  };

  const handleReset = () => {
    resetFormFields();
    homePrefillAppliedRef.current = false;
  };

  const renderHighlightedText = (textValue) => {
    const parts = buildHighlightedParts(textValue, searchWord);
    const searchTerm = searchWord.trim().toLowerCase();

    return parts.map((part, index) => {
      if (searchTerm && part.toLowerCase() === searchTerm) {
        return (
          <span key={`${part}-${index}`} className="highlight">
            {part}
          </span>
        );
      }

      return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
    });
  };

  const getReferenceText = (verse) => {
    const originalBookName = verse.book_name;

    const bookNamesByLanguage = {
      tamil: getTamilBookName(originalBookName),
      english: getEnglishBookName(originalBookName),
      deutsch: getDeutschBookName(originalBookName),
    };

    const bookNames = selectedLanguages
      .map((lang) => bookNamesByLanguage[lang])
      .filter(Boolean)
      .join(" | ");

    return `${bookNames} ${verse.chapter_number} : ${verse.verse_number}`;
  };

  const renderVerse = (verse, index) => {
    return (
      <div
        key={`${verse.book_name}-${verse.chapter_number}-${verse.verse_number}-${index}`}
        className="verse-combined-column"
      >
        <p className="searchword-reference-line">{getReferenceText(verse)}</p>

        {selectedLanguages.map((lang) => (
          <p key={lang} className="verse-line">
            <span className="searchword-verse-text">
              {renderHighlightedText(verse[lang] || "")}
            </span>
          </p>
        ))}
      </div>
    );
  };

  const renderResultsSummary = () => {
    if (!verses.length) return null;

    return (
      <div className="searchword-results-summary" ref={resultSummaryRef}>
        <div className="searchword-results-summary-main">
          <div className="searchword-results-summary-title">
            {labels.searchResult}
          </div>

          <div className="searchword-results-summary-word">
            <span>{labels.wordLabel} :</span>
            <strong>{selectedSearchWord}</strong>
          </div>
        </div>

        <div className="searchword-results-summary-counts">
          <div className="searchword-results-summary-count-card">
            <span className="searchword-results-summary-count-label">
              {labels.oldTestament}
            </span>
            <span className="searchword-results-summary-count-number">
              {resultCounts.old}
            </span>
          </div>

          <div className="searchword-results-summary-count-card">
            <span className="searchword-results-summary-count-label">
              {labels.newTestament}
            </span>
            <span className="searchword-results-summary-count-number">
              {resultCounts.new}
            </span>
          </div>

          <div className="searchword-results-summary-total-card">
            <span className="searchword-results-summary-count-label">
              {labels.found}
            </span>
            <span className="searchword-results-summary-count-number">
              {resultCounts.total}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`searchword-container ${
        verses.length > 0 ? "searchword-result-active" : ""
      } ${isNativeInputFocused ? "searchword-native-input-focused" : ""}`}
      ref={pageScrollRef}
    >
      {alertMessage ? (
        <div className="searchword-alert-overlay">
          <div className="searchword-alert-box">
            <div className="searchword-alert-title">{labels.alertTitle}</div>
            <div className="searchword-alert-message">{alertMessage}</div>

            <div className="searchword-alert-actions">
              <button
                type="button"
                className="searchword-alert-button"
                onClick={() => setAlertMessage("")}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="searchword-form-container" ref={formContainerRef}>
        <form onSubmit={handleSubmit}>
          <div className="searchword-form-row">
            <label htmlFor="searchword-testament">{labels.testament}</label>

            <CustomSelect
              id="searchword-testament"
              value={selectedTestament}
              options={testamentOptions}
              placeholder={allSelectText}
              onChange={handleTestamentChange}
            />
          </div>

          <div className="searchword-form-row">
            <label htmlFor="searchword-book">{labels.book}</label>

            <CustomSelect
              id="searchword-book"
              value={selectedBook}
              options={bookOptions}
              placeholder={allSelectText}
              onChange={handleBookChange}
              disabled={!selectedTestament || selectedTestament === ALL_VALUE}
            />
          </div>

          <div className="searchword-form-row">
            <label htmlFor="searchword-chapter">{labels.chapter}</label>

            <CustomSelect
              id="searchword-chapter"
              value={selectedChapter}
              options={chapterOptions}
              placeholder={allSelectText}
              onChange={handleChapterChange}
              disabled={!selectedBook || selectedBook === ALL_VALUE}
            />
          </div>

          <div
            className="searchword-form-row searchword-input-row"
            ref={searchInputRowRef}
          >
            <label htmlFor="searchword-word">{labels.searchWord}</label>

            <div className="searchword-input-wrap">
              <input
                ref={searchInputRef}
                id="searchword-word"
                type="text"
                dir="ltr"
                value={searchWord}
                onTouchStart={rememberSearchWordInputScrollPosition}
                onMouseDown={rememberSearchWordInputScrollPosition}
                onFocus={handleNativeInputFocus}
                onBlur={handleNativeInputBlur}
                onChange={(event) => setSearchWord(event.target.value)}
                placeholder={labels.searchWordPlaceholder}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                style={
                  isTamilDefaultLanguage ? undefined : { paddingRight: "14px" }
                }
              />

              {isTamilDefaultLanguage ? (
                <button
                  ref={keyboardToggleRef}
                  type="button"
                  className={`searchword-keyboard-toggle ${
                    showKeyboard ? "searchword-keyboard-toggle-active" : ""
                  }`}
                  onClick={handleKeyboardToggle}
                  aria-label={labels.keyboard}
                  title={labels.keyboard}
                >
                  <FaKeyboard />
                </button>
              ) : null}
            </div>
          </div>

          <div className="searchword-form-row searchword-language-row">
            <label>{labels.language}</label>

            <div className="searchword-language-selection">
              {orderedLanguages.map((lang) => (
                <label key={lang}>
                  <input
                    type="checkbox"
                    value={lang}
                    checked={selectedLanguages.includes(lang)}
                    onChange={() => toggleLanguage(lang)}
                  />
                  {getLanguageText(lang)}
                </label>
              ))}
            </div>
          </div>

          <div className="searchword-submit-reset-buttons-column">
            <button type="submit" className="searchword-submit-button">
              {labels.submit}
            </button>

            <button
              type="button"
              onClick={handleReset}
              className="searchword-reset-button"
            >
              {labels.reset}
            </button>
          </div>
        </form>

        {showKeyboard && isTamilDefaultLanguage ? (
          <div
            className="searchword-keyboard-panel"
            ref={keyboardPanelRef}
            style={{
              top: `${keyboardPosition.top}px`,
              maxHeight: keyboardPosition.ready
                ? `${keyboardPosition.maxHeight}px`
                : "none",
              opacity: keyboardPosition.ready ? 1 : 0,
              pointerEvents: keyboardPosition.ready ? "auto" : "none",
            }}
          >
            <button
              type="button"
              className="searchword-keyboard-close"
              onClick={closeKeyboard}
              aria-label={labels.closeKeyboard}
              title={labels.closeKeyboard}
            >
              <FaTimes />
            </button>

            <TamilKeyboard
              onKeyPress={handleKeyboardKeyPress}
              resetSignal={resetSignal}
            />
          </div>
        ) : null}
      </div>

      <div className="searchword-result-container" ref={resultContainerRef}>
        {verses.length > 0 ? (
          <div className="searchword-results-list-wrap">
            {renderResultsSummary()}

            <div className="searchword-results-list">
              {verses.map((verse, index) => renderVerse(verse, index))}
            </div>
          </div>
        ) : (
          <p>{isSubmitted && error ? error : labels.note}</p>
        )}
      </div>
    </div>
  );
}

export default SearchWord;